import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle2, FileText, MessageSquare, Clock, ArrowRight, Mail } from 'lucide-react';

const formatCurrency = (amount) => {
  if (amount >= 1000000) {
    return `£${(amount / 1000000).toFixed(1)}M`;
  }
  return `£${(amount / 1000).toFixed(0)}K`;
};

export default function ThankYouPage() {
  const location = useLocation();
  const applicationData = location.state || {};

  const nextSteps = [
    {
      icon: Mail,
      title: 'Advisor Contact',
      description: 'A dedicated advisor will reach out within 4 hours to discuss your application.',
      time: 'Within 4 hours'
    },
    {
      icon: FileText,
      title: 'Document Upload',
      description: 'Log into your portal to upload the required documents.',
      time: 'As soon as possible'
    },
    {
      icon: MessageSquare,
      title: 'Application Review',
      description: "We'll review your application and documents, then send you an offer.",
      time: '24-48 hours'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="thank-you-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl font-['Outfit']">Q</span>
            </div>
            <span className="text-xl font-bold text-slate-900 font-['Outfit']">QuickFinance</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12 sm:py-20">
        {/* Success Banner */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-blue-700" />
          </div>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 font-['Outfit']">
            Application Submitted Successfully!
          </h1>
          
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            Thank you for choosing QuickFinance. Your application has been received and is being processed.
          </p>
        </div>

        {/* Application Summary */}
        {applicationData.loan_amount && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-6 font-['Outfit']">Your Application Summary</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-sm text-blue-700 mb-1">Loan Amount</p>
                <p className="text-2xl font-bold text-blue-700 font-['Outfit']">
                  {formatCurrency(applicationData.loan_amount)}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-500 mb-1">Business Type</p>
                <p className="font-semibold text-slate-900">{applicationData.business_type || 'N/A'}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-500 mb-1">Loan Type</p>
                <p className="font-semibold text-slate-900">{applicationData.loan_type || 'N/A'}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-500 mb-1">Purpose</p>
                <p className="font-semibold text-slate-900">{applicationData.funding_purpose || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Portal Access */}
        <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 rounded-2xl p-6 sm:p-8 text-white mb-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-xl font-bold mb-2 font-['Outfit']">Your Client Portal is Ready</h2>
              <p className="text-blue-200">
                Check your email for login details. Upload documents and track your application progress.
              </p>
            </div>
            <Link to="/portal/login">
              <Button className="bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap" data-testid="go-to-portal-btn">
                Go to Portal <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-6 font-['Outfit']">What Happens Next?</h2>
          
          <div className="space-y-6">
            {nextSteps.map((step, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <step.icon className="w-6 h-6 text-blue-700" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-slate-900">{step.title}</h3>
                    <span className="text-sm text-blue-700 font-medium flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {step.time}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-slate-100 rounded-2xl p-6 text-center">
          <p className="text-slate-600 mb-4">Questions? Our team is here to help.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:hello@quickfinance.io" className="flex items-center gap-2 text-slate-700 hover:text-blue-700">
              <Mail className="w-4 h-4" />
              hello@quickfinance.io
            </a>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link to="/" className="text-slate-600 hover:text-blue-700">
            ← Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
}
